/**
 * @author       Andreas Bayer <andreas@plusbyte.de>
 * @copyright    2015 Plusbyte UG (haftungsbeschränkt)
 * @license      {@link https://plusbyte.de Plusbyte License}
 */

import { type BotPacingProfile, sampleBotThinkDelayMs } from './botPacing';
import { View } from './interfaces';
import { gameLogger } from './logger';
import Turfmaster, { Horse, StateMachineValue } from './tmmodel';
import { stateToString, wait } from './utilConstants';
import { randomBool, randomInt } from './utilRandom';

/**
 * Computer
 * @class Turfmaster.Computer
 * @constructor
 * @param {Number} playerid - ID of the player
 * @param {Turfmaster.compStrengths} strength - Strength of the Computer
 * @param {Turfmaster.Controller} controller - Controller object
 * @param pacingProfile - Base ms and multipliers for think-time sampling; use pacingProfileFromBaseDelayMs(0) for fast simulations
 */
class Computer implements View {
  /**
   * @property {Turfmaster.Controller} controller - Controller object
   */
  public controller: any;

  /**
   * @property {Number} playerid - ID of the player
   */
  public playerid: number;

  /**
   * @property {Turfmaster.compStrengths} strength - Strength of the Computer
   */
  public strength: number;

  /**
   * @property pacingProfile - Base ms and tier ranges for think delays (see sampleBotThinkDelayMs)
   */
  public pacingProfile: BotPacingProfile;

  public constructor(
    playerid: number,
    strength: number,
    controller: any,
    pacingProfile: BotPacingProfile,
  ) {
    this.controller = controller;
    this.playerid = playerid;
    this.strength = strength;
    this.pacingProfile = pacingProfile;

    // TODO: create timeout that repeatedly checks if something can be done, i.e. reacted to a message
    // TODO: add logic for starting a game within receiveMsg
  }

  /**
   * ReceiveMsg
   * @method Turfmaster.Controller#receiveMsg
   * @param {Turfmaster.stateMachine} state - State
   * @param {Object} params - Params
   */
  public receiveMsg = async (state: StateMachineValue, params: any): Promise<void> => {
    //console.log('<' + this.playerid + ':receive> ' + stateToString(state) + ' ' + JSON.stringify(params));

    switch (state) {
      case Turfmaster.stateMachine.MERGECARDS:
        await this.mergeCardsEvent();
        break;
      case Turfmaster.stateMachine.SELECTCARDDICE:
        await this.selectCardDiceEvent();
        break;
      case Turfmaster.stateMachine.SELECTMOVE:
        // Dice turn: Computer chooses from provided moves, OR card turn notification
        await this.selectMoveEvent(params.moves);
        break;
      case Turfmaster.stateMachine.SELECTDICES:
        await this.selectDicesEvent();
        break;
    }
  };

  /**
   * SendMsg
   * @method Turfmaster.Controller#sendMsg
   * @param {Turfmaster.stateMachine} state - State
   * @param {Object} params - Params
   */
  public sendMsg = async (state: StateMachineValue, params: any): Promise<false | void> => {
    gameLogger.debug(
      { playerid: this.playerid, state: stateToString(state), params },
      'computer send',
    );

    switch (state) {
      case Turfmaster.stateMachine.MERGECARDS:
      case Turfmaster.stateMachine.SELECTCARDDICE:
      case Turfmaster.stateMachine.SELECTMOVE:
      case Turfmaster.stateMachine.SELECTDICES:
        params.playerid = this.playerid;
        // Get userid from the player model - required by controller after migration
        params.userid = this.controller.game.players[this.playerid]?.userid;

        await this.controller.receiveMsg(state, params);
        break;
    }
  };

  /**
   * MergeCardsEvent
   * @method Turfmaster.Controller#mergeCardsEvent
   */
  public mergeCardsEvent = async () => {
    const _this = this;
    const horseid = this.controller.getHorseId();

    if (this.controller.getPlayerId() !== this.playerid) {
      return false;
    }

    // add some waiting time to fake the computer thinking
    await wait(sampleBotThinkDelayMs(this.pacingProfile, 'mergeCards'));

    await _this.sendMsg(Turfmaster.stateMachine.MERGECARDS, {
      horseid: horseid,
      added: [],
      removed: [],
    });
  };

  /**
   * SelectCardDiceEvent
   * @method Turfmaster.Controller#selectCardDiceEvent
   */
  public selectCardDiceEvent = async () => {
    const _this = this;
    const horseid = this.controller.getHorseId();

    if (this.controller.getPlayerId() !== this.playerid) {
      return false;
    }

    // add some waiting time to fake the computer thinking
    await wait(sampleBotThinkDelayMs(this.pacingProfile, 'cardSelect'));

    let carddiceid = 0;

    if (this.controller.game.turntype === Turfmaster.turntypes.CARDS) {
      const cards = this.controller.getCurrentCards(horseid);
      switch (this.strength) {
        case Turfmaster.compStrengths.EASY:
          carddiceid = randomInt(0, cards.length - 1);
          break;
        case Turfmaster.compStrengths.MEDIUM: {
          carddiceid = 0;
          let found = false;
          // On first line horse must use cards smaller or equal than 6 or joker on line 7 or 8
          if (
            this.controller.game.horses[horseid].field.prog < 15 &&
            this.controller.game.horses[horseid].field.lane > 5
          ) {
            out: for (let i = 0; i < cards.length; i++) {
              if (cards[i] <= 6) {
                const moves = this.controller.getMovesLogic(horseid, i, false);
                for (let j = 0; j < moves.length; j++) {
                  if (!moves[j].drop) {
                    carddiceid = i;
                    found = true;
                    break out;
                  }
                }
              }
            }
          }
          // On first line horse must use cards 6, 7 or 8 on line 1 to 6
          if (
            this.controller.game.horses[horseid].field.prog < 15 &&
            this.controller.game.horses[horseid].field.lane <= 5
          ) {
            const cardSelection: number[] = [];

            for (let i = 0; i < cards.length; i++) {
              if (cards[i] >= 6 && cards[i] <= 8) {
                const moves = this.controller.getMovesLogic(horseid, i, false);
                for (let j = 0; j < moves.length; j++) {
                  if (!moves[j].drop) {
                    cardSelection.push(i);
                    break;
                  }
                }
              }
            }

            if (cardSelection.length > 0) {
              carddiceid = cardSelection[randomInt(0, cardSelection.length - 1)];

              found = true;
            }
          }
          // beyond field 60, horse will randomly prefer joker
          if (this.controller.game.horses[horseid].field.prog > 60 && randomBool()) {
            out: for (let i = 0; i < cards.length; i++) {
              if (cards[i] < 0) {
                const moves = this.controller.getMovesLogic(horseid, i, false);
                for (let j = 0; j < moves.length; j++) {
                  if (!moves[j].drop) {
                    carddiceid = i;
                    found = true;
                    break out;
                  }
                }
              }
            }
          }
          // Normally horse will use highest card with has no handicap and will not drop the horse
          if (!found) {
            out: for (let i = cards.length - 1; i >= 0; i--) {
              if (!this.controller.isHandi(horseid, cards[i])) {
                const moves = this.controller.getMovesLogic(horseid, i, false);
                for (let j = 0; j < moves.length; j++) {
                  if (!moves[j].drop) {
                    carddiceid = i;
                    found = true;
                    break out;
                  }
                }
              }
            }
          }
          // May be not used
          if (!found) {
            out: for (let i = cards.length - 1; i >= 0; i--) {
              const moves = this.controller.getMovesLogic(horseid, i, false);
              for (let j = 0; j < moves.length; j++) {
                if (!moves[j].drop) {
                  carddiceid = i;
                  break out;
                }
              }
            }
          }
          break;
        }
      }
    } else {
      carddiceid = randomInt(0, this.controller.getDices().length - 1);
    }

    const moves = this.controller.getMovesLogic(horseid, carddiceid, false);
    let moveid = 0; // start with valid moveid (0) even when no moves; controller will advance turn

    // In dice turns, the dice is fixed by the throwing player; bot cannot retry another dice.
    // Bot MUST send SELECTMOVE even if no moves exist (moves.length === 0) or all moves are drops.
    // Controller handles empty moves by advancing turn gracefully (see selectMoveEvent).
    if (moves && moves.length > 0) {
      switch (this.strength) {
        case Turfmaster.compStrengths.EASY:
          moveid = randomInt(0, moves.length - 1);
          break;
        case Turfmaster.compStrengths.MEDIUM: {
          moveid = 0; // Initialize; remains 0 if movesSelected is empty (all moves are drops)

          const movesSelected: { [key: number]: any } = {};

          for (let i = 0; i < moves.length; i++) {
            if (!moves[i].drop) {
              if (Object.keys(movesSelected).length === 0) {
                movesSelected[i] = moves[i];
              } else {
                const moveField = moves[i].fields[moves[i].fields.length - 1];

                for (const key in movesSelected) {
                  const moveSelectedField =
                    movesSelected[key].fields[movesSelected[key].fields.length - 1];

                  if (
                    moveField.lane < moveSelectedField.lane ||
                    moveField.prog > moveSelectedField.prog
                  ) {
                    movesSelected[i] = moves[i];
                  }
                }
              }
            }
          }

          const keys = Object.keys(movesSelected);
          if (keys.length > 0) {
            moveid = Number(keys[randomInt(0, keys.length - 1)]);
          }
          // If keys.length === 0 (all moves were drops), moveid remains 0 (initialized value)
          break;
        }
      }
    }

    // Card turn: Send complete card+move decision to controller
    await _this.sendMsg(Turfmaster.stateMachine.SELECTMOVE, {
      horseid: horseid,
      carddiceid: carddiceid,
      moveid,
    });
  };

  /**
   * SelectMoveEvent
   * @method Turfmaster.Controller#selectMoveEvent
   * @param {Array} moves - Moves
   */
  public selectMoveEvent = async (moves: any) => {
    const _this = this;
    const horseid = this.controller.getHorseId();

    if (this.controller.getPlayerId() !== this.playerid) {
      return false;
    }

    // add some waiting time to fake the computer thinking
    await wait(sampleBotThinkDelayMs(this.pacingProfile, 'moveSelect'));

    let moveid = 0;

    switch (this.strength) {
      case Turfmaster.compStrengths.EASY:
        moveid = randomInt(0, moves.length - 1);
        break;
      case Turfmaster.compStrengths.MEDIUM: {
        // move on with moveid == 0
        const movesSelected: { [key: number]: any } = {};

        for (let i = 0; i < moves.length; i++) {
          if (!moves[i].drop) {
            if (Object.keys(movesSelected).length === 0) {
              movesSelected[i] = moves[i];
            } else {
              const moveField = moves[i].fields[moves[i].fields.length - 1];

              for (const key in movesSelected) {
                const moveSelectedField =
                  movesSelected[key].fields[movesSelected[key].fields.length - 1];

                if (
                  moveField.lane < moveSelectedField.lane ||
                  moveField.prog > moveSelectedField.prog
                ) {
                  movesSelected[i] = moves[i];
                }
              }
            }
          }
        }

        moveid = Number(
          Object.keys(movesSelected)[randomInt(0, Object.keys(movesSelected).length - 1)],
        );

        break;
      }
    }

    // Dice turn: Respond with move choice. Card turn: No response needed (just notification)
    if (this.controller.game.turntype === Turfmaster.turntypes.DICES) {
      await _this.sendMsg(Turfmaster.stateMachine.SELECTMOVE, {
        horseid: horseid,
        carddiceid: -1,
        moveid,
      });
    }
  };

  /**
   * SelectDicesEvent
   * @method Turfmaster.Controller#selectDicesEvent
   */
  public selectDicesEvent = async () => {
    const _this = this;

    if (this.controller.game.turndiceplayerid !== this.playerid) {
      return false;
    }

    // add some waiting time to fake the computer thinking
    // Placed here (acting bot only) so controller broadcasts aren't blocked by bots.
    await wait(sampleBotThinkDelayMs(this.pacingProfile, 'dicePickVisibility'));

    let dicesid = 0;

    switch (this.strength) {
      case Turfmaster.compStrengths.EASY:
        // select only one dice, so the selection is never above the handicap (which is illegal during dice rounds, according to the rules)
        dicesid = randomInt(0, this.controller.game.turndices.length - 1); // will be 0 or 1 (as turndices.length is usually 2)
        break;
      case Turfmaster.compStrengths.MEDIUM: {
        const horseWithHandi = this.controller.game.horses.reduce(
          (prev: boolean, horse: Horse, horseIndex: number) => {
            return prev || this.controller.isHandi(horseIndex, this.controller.calcDicessum());
          },
          false,
        );

        // if spieler hat mindestens 1 pferd mit handicap > würfelsumme )
        if (horseWithHandi) {
          // falls ja: nimm nur 1 würfel
          dicesid = this.getOneDice();
        } else {
          // falls nein: nimm 50% beide würfel und 50% einen
          if (randomBool()) {
            dicesid = this.controller.game.turndices.length;
          } else {
            dicesid = this.getOneDice();
          }
        }
        break;
      }
    }

    await _this.sendMsg(Turfmaster.stateMachine.SELECTDICES, { dicesid });
  };

  private getOneDice = () => {
    // "einen würfel nehmen" = 75% größeren würfel nehmen, 25% kleineren
    const smalerDice = randomInt(0, 3) === 0;
    if (smalerDice) {
      return this.controller.game.turndices[0] < this.controller.game.turndices[1] ? 0 : 1;
    } else {
      return this.controller.game.turndices[0] > this.controller.game.turndices[1] ? 0 : 1;
    }
  };
}

export default Computer;
